// @ts-nocheck

import { reduce } from 'lodash';
import { batch } from 'react-redux';
import v4 from 'uuid/v4';

import { flashMessageActionCreators } from '../actions';
import {
  basketAddItem,
  basketAddItems,
  basketClear,
  basketDelItem,
  basketOutcomeSelected,
  basketRemoveCachedMMAPIResponseInfos,
  basketSwitchMode,
  basketUpdateItemIsFromMultichoice,
} from '../actions/basket';
import { doPullBasket } from '../actions/messages';
import {
  getFutureOdds,
  myMatchSplit,
  requestMyMatchAddWithMerge,
  requestMyMatchDeletionWithMerge,
  requestMyMatchMerge,
  requestMyMatchOutcomeDeletion,
  requestMyMatchOutcomeInjection,
  requestMyMatchSplit,
} from '../actions/mymatch';
import { type CustomRouterHistory } from '../createHistory';
import { __ } from '../i18n/l10n';
import { makeMatchIsMyMatchAvailableSelector } from '../selectors/mymatch.selector';
import { type BasketItemState } from '../states/basket';
import { BasketActionTypes } from '../types/actions/basket';
import { MyMatchActionTypes } from '../types/actions/mymatch';
import { NetworkActionTypes } from '../types/actions/network';
import { BasketModes, BasketStatuses } from '../types/basket';
import { FlashMessageScopes, FlashMessageSeverities, FlashMessageTypes } from '../types/flashmessage';
import { type StoreType, type GenericAction } from '../types/redux';
import { RoutePatterns } from '../types/routes';

const handledActionTypes = [
  BasketActionTypes.OUTCOME_SELECTED,
  BasketActionTypes.OUTCOME_UNSELECTED,
  BasketActionTypes.REQUEST_SWITCH_MODE,
  BasketActionTypes.CLEAR,
  BasketActionTypes.ADD_ITEM,
  BasketActionTypes.ADD_ITEMS,
  BasketActionTypes.DEL_ITEM,
  BasketActionTypes.UPDATE_ITEM_IS_FROM_MULTICHOICE,
  MyMatchActionTypes.MERGE,
  MyMatchActionTypes.SPLIT,
  BasketActionTypes.SUBMIT_OK,
  BasketActionTypes.GET_SELECTION,
  BasketActionTypes.IMPORT_ITEMS,
  NetworkActionTypes.RECV,
];

let basketRequestId = null;

const basketMiddleware =
  (history: CustomRouterHistory) =>
  ({ getState, dispatch }: StoreType) =>
  (next: (action: GenericAction) => unknown) =>
  async (action: GenericAction) => {
    const { type: actionType, payload: actionPayload } = action;
    const state = getState();
    const { basket: basketState } = state;

    if (handledActionTypes.indexOf(actionType) !== -1) {
      if (actionType === BasketActionTypes.IMPORT_ITEMS) {
        // On clear le panier si il est en mode VALIDATED (confirmation de pari) avant d'ajouter les nouvelles selections
        if (BasketStatuses.VALIDATED === basketState.status) {
          dispatch(basketClear());
        }

        basketRequestId = v4();

        dispatch(
          basketAddItems({
            bets: actionPayload?.items,
            betslipReference: actionPayload?.betslipReference,
            sourceElementUuid: actionPayload?.uuid,
            isRepeatBet: actionPayload?.isRepeatBet,
            isFromHistory: actionPayload?.isFromHistory,
          }),
        );

        dispatch(doPullBasket({ requestId: basketRequestId, forceData: true }));
      }

      // On doit vérifier si les selections ajoutées au panier sont disponibles
      // Si une selection n'est pas disponible, on doit la retirer du panier
      // On doit aussi afficher un message d'erreur pour informer l'utilisateur
      if (actionType === NetworkActionTypes.RECV) {
        if (actionPayload?.requestId === basketRequestId) {
          const { matches, bets, odds } = actionPayload ?? {};
          const { basket } = getState();
          const { items } = basket;

          const unavailableBetsCount = reduce(
            items,
            (unavailable, { matchId, betId, oddId }) => {
              let itemUnavailable = unavailable;
              if (matches[matchId] === null || bets[betId] === null || odds[oddId] === null) {
                dispatch(basketDelItem({ oddId, matchId }));
                itemUnavailable++;
              }

              return itemUnavailable;
            },
            0,
          );

          if (unavailableBetsCount > 0) {
            dispatch(
              flashMessageActionCreators.addMessage({
                scope: FlashMessageScopes.BASKET,
                severity: FlashMessageSeverities.ERROR,
                type: FlashMessageTypes.DISMISS_MANUAL,
                // __('%1$s selection could not be added', [unavailableBetsCount], '%1$s selections could not be added')
                message: __(
                  '%1$s selection could not be added',
                  [unavailableBetsCount],
                  '%1$s selections could not be added',
                ),
              }),
            );
          }
        }
      }

      if (actionType === BasketActionTypes.OUTCOME_SELECTED) {
        // Si le panier est en mode VALIDATED (confirmation de pari), on le clear avant d'ajouter la nouvelle selection
        if (BasketStatuses.VALIDATED === basketState.status) {
          await dispatch(basketClear());
        }

        const {
          oddId: actionPayloadOddId,
          matchId: actionPayloadMatchId,
          betId: actionPayloadBetId,
          sourceElementUuid: actionPayloadSourceElementUuid,
          isFromLineUp: actionPayloadIsFromLineUp,
          isFromMultichoice: actionPayloadIsFromMultichoice,
          isRepeatBet: actionPayloadIsRepeatBet,
          isFromHistory: actionPayloadIsFromHistory,
        } = actionPayload ?? {};
        const { basket: newBasketState } = getState();
        const { items: prevItems, status: basketStatus } = newBasketState;

        if (!~[BasketStatuses.PENDING, BasketStatuses.WAITING].indexOf(basketStatus)) {
          const prevItemsAsArray: Array<BasketItemState> = Object.keys(prevItems).map((key) => prevItems[key]);
          const basketItemWithSameMatchId = prevItemsAsArray.find((item) => item.matchId === actionPayloadMatchId);
          const isMyMatchAvailable = makeMatchIsMyMatchAvailableSelector()(state, { matchId: actionPayloadMatchId });

          if (basketItemWithSameMatchId && isMyMatchAvailable) {
            const { betbuilder: basketItemIsMyMatch, oddId: basketItemOddId } = basketItemWithSameMatchId;

            if (basketItemIsMyMatch) {
              // Si un item MyMatch liée au match selectionné existe déjà
              // On doit injecter ce nouvel item dans la selection MyMatch existante

              dispatch(
                requestMyMatchOutcomeInjection({
                  oddId: actionPayloadOddId,
                  matchId: actionPayloadMatchId,
                  betId: actionPayloadBetId,
                  sourceElementUuid: actionPayloadSourceElementUuid,
                  isFromMultichoice: actionPayloadIsFromMultichoice,
                  isRepeatBet: actionPayloadIsRepeatBet,
                }),
              );
            } else {
              // Si un item classique liée au match selectionné existe déjà

              if (basketItemOddId === actionPayloadOddId) {
                // Si c'est le même item que celui sélectionné c'est qu'on essaye d'intervertir classique / multichance

                dispatch(
                  basketUpdateItemIsFromMultichoice({
                    oddId: actionPayloadOddId,
                    isFromMultichoice: actionPayloadIsFromMultichoice,
                  }),
                );
              } else {
                // On doit fusionner l’ancien item et le nouveau en un item MyMatch

                dispatch(
                  requestMyMatchAddWithMerge({
                    oddId: actionPayloadOddId,
                    matchId: actionPayloadMatchId,
                    betId: actionPayloadBetId,
                    sourceElementUuid: actionPayloadSourceElementUuid,
                    isFromLineUp: actionPayloadIsFromLineUp,
                    isFromMultichoice: actionPayloadIsFromMultichoice,
                    isRepeatBet: actionPayloadIsRepeatBet,
                  }),
                );
              }
            }
          } else {
            // Si aucun item liée au match selectionné n’existe
            // Ajout classique de la selection au panier

            dispatch(
              basketAddItem({
                oddId: actionPayloadOddId,
                matchId: actionPayloadMatchId,
                betId: actionPayloadBetId,
                sourceElementUuid: actionPayloadSourceElementUuid,
                isFromLineUp: actionPayloadIsFromLineUp,
                isFromMultichoice: actionPayloadIsFromMultichoice,
                isRepeatBet: actionPayloadIsRepeatBet,
                isFromHistory: actionPayloadIsFromHistory,
              }),
            );
          }
        }
      }

      if (actionType === BasketActionTypes.OUTCOME_UNSELECTED) {
        const {
          oddId: actionPayloadOddId,
          matchId: actionPayloadMatchId,
          isFromMultichoice: actionPayloadIsFromMultichoice,
        } = actionPayload ?? {};
        const { items: prevItems, status: basketStatus } = basketState;
        const prevItemsAsArray: Array<BasketItemState> = Object.keys(prevItems).map((key) => prevItems[key]);
        const basketItemsWithSameMatchId = prevItemsAsArray.filter((item) => item.matchId === actionPayloadMatchId);
        const isMyMatchAvailable = makeMatchIsMyMatchAvailableSelector()(state, { matchId: actionPayloadMatchId });
        const target = prevItems[actionPayloadOddId];

        if (!~[BasketStatuses.PENDING, BasketStatuses.WAITING].indexOf(basketStatus)) {
          if (target?.betbuilder) {
            batch(() => {
              dispatch(basketDelItem({ oddId: actionPayloadOddId, matchId: actionPayloadMatchId }));
              dispatch(basketRemoveCachedMMAPIResponseInfos({ matchId: actionPayloadMatchId }));
            });
          } else {
            if (basketItemsWithSameMatchId.length) {
              const myMatchBasketItemWithSameMatchId = basketItemsWithSameMatchId.find(
                (item) => item.betbuilder && Array.isArray(item.selectionItems),
              );

              if (myMatchBasketItemWithSameMatchId) {
                // Si un item MyMatch liée au match deselectionné existe déjà

                const {
                  oddId: basketItemWithSameMatchIdOddId,
                  selectionItems: basketItemWithSameMatchIdSelectionItems = [],
                } = myMatchBasketItemWithSameMatchId;
                const itemFoundInOutcomeIds = actionPayloadIsFromMultichoice
                  ? !!basketItemWithSameMatchIdSelectionItems.find((_item) => {
                      return Array.isArray(_item.outcomeIds) && !!~_item.outcomeIds.indexOf(actionPayloadOddId);
                    })
                  : false;
                const nbTotalItemsInSelection = basketItemWithSameMatchIdSelectionItems.reduce(
                  (acc, _selectionItem) => acc + (_selectionItem.outcomeIds?.length ?? 1),
                  0,
                );

                if (isMyMatchAvailable && nbTotalItemsInSelection > 2) {
                  // Si cet item possède une selection de plus de 2 items (3+)
                  // OU
                  // Si cet item possède une selection de moins de 3 items mais comprenant l'odds recherchée dans une liste multichance
                  // On essaye de retirer l’item concerné de la selection MyMatch OU de la liste multichance)

                  dispatch(
                    requestMyMatchOutcomeDeletion({
                      myMatchBasketItemId: basketItemWithSameMatchIdOddId,
                      selectionItemOddId: actionPayloadOddId,
                      isFromMultichoice: actionPayloadIsFromMultichoice,
                      itemFoundInOutcomeIds,
                    }),
                  );
                } else if (nbTotalItemsInSelection === 2 || !isMyMatchAvailable) {
                  // Si cet item possède une selection de 2 items ou que le myMatch n'est pas disponible pour ce match
                  // On doit supprimer l’item MyMatch puis injecter les items restants comme des selections classiques

                  const restingSelectionItems = basketItemWithSameMatchIdSelectionItems.reduce((acc, selectionItem) => {
                    const {
                      oddId: selectionItemOddId,
                      outcomeId: selectionItemOutcomeId,
                      outcomeIds: selectionItemOutcomeIds,
                    } = selectionItem;

                    if (selectionItemOutcomeIds) {
                      const nextOutcomeIds = selectionItemOutcomeIds.filter(
                        (_outcomeId) => _outcomeId !== actionPayloadOddId,
                      );

                      const nextItem = { ...selectionItem };

                      if (nextOutcomeIds.length === 1) {
                        nextItem.outcomeId = nextOutcomeIds[0];
                        nextItem.isFromMultichoice = true;
                        delete nextItem.outcomeIds;
                      } else {
                        nextItem.outcomeIds = nextOutcomeIds;
                      }

                      return [...acc, { ...nextItem }];
                    } else {
                      const selectionItemOddsId = selectionItemOddId ?? selectionItemOutcomeId;

                      if (!!selectionItemOddsId && selectionItemOddsId !== actionPayloadOddId) {
                        return [...acc, selectionItem];
                      }
                    }

                    return acc;
                  }, []);

                  if (restingSelectionItems.length) {
                    dispatch(
                      myMatchSplit({
                        itemIdToRemove: basketItemWithSameMatchIdOddId,
                        splitedItems: restingSelectionItems,
                        matchId: actionPayloadMatchId,
                      }),
                    );
                  }
                } else {
                  batch(() => {
                    dispatch(basketDelItem({ oddId: basketItemWithSameMatchIdOddId, matchId: actionPayloadMatchId }));
                    dispatch(basketRemoveCachedMMAPIResponseInfos({ matchId: actionPayloadMatchId }));
                  });
                }
              } else {
                // Si aucun item MyMatch lié au match deselectionné et un ou plusieurs items classiques liées au match (et myMatch disponible)
                // deselectionné existe déjà on doit deselectionner l’item du panier

                if (basketItemsWithSameMatchId.length > 2 && isMyMatchAvailable) {
                  // Il restera au moins 2 items après la suppression de l'item, il faut donc retester l'éligibilité à MyMatch
                  // sur le reste des items et éventuellement les fusionner

                  dispatch(
                    requestMyMatchDeletionWithMerge({ oddId: actionPayloadOddId, matchId: actionPayloadMatchId }),
                  );
                } else {
                  batch(() => {
                    dispatch(basketDelItem({ oddId: actionPayloadOddId, matchId: actionPayloadMatchId }));
                    dispatch(basketRemoveCachedMMAPIResponseInfos({ matchId: actionPayloadMatchId }));
                  });
                }
              }
            }
          }
        }
      }

      if (actionType === BasketActionTypes.REQUEST_SWITCH_MODE) {
        // On doit rassembler ou éclater les selections en fonction du mode selectionné

        const { items: prevItems } = basketState;
        const prevItemsAsArray: Array<BasketItemState> = Object.keys(prevItems).map((key) => prevItems[key]);

        const groupedClassicItemsByMatchId = {};
        const groupedMyMatchItemsByMatchId = [];

        for (const item of prevItemsAsArray) {
          const { matchId, betbuilder } = item;

          if (!betbuilder) {
            if (groupedClassicItemsByMatchId[matchId]) {
              groupedClassicItemsByMatchId[matchId].push(item);
            } else {
              groupedClassicItemsByMatchId[matchId] = [item];
            }
          } else {
            groupedMyMatchItemsByMatchId.push(item);
          }
        }

        const { mode } = actionPayload ?? {};

        if (mode === BasketModes.SIMPLE) {
          // Mode simple demandé
          // Il faut éclater les selections MyMatch

          const promises = groupedMyMatchItemsByMatchId.map(
            (group) =>
              new Promise((res) => {
                const { oddId } = group;

                dispatch(
                  requestMyMatchSplit(
                    { oddId },
                    {
                      onFinally: res,
                    },
                  ),
                );
              }),
          );

          try {
            await Promise.all(promises);
          } finally {
            dispatch(basketSwitchMode({ mode }));
          }
        } else {
          // Mode combiné ou système demandé
          // Il faut rassembler les selections classiques d'un même match en un item MyMatch

          const classicMatchIds = Object.keys(groupedClassicItemsByMatchId);

          const promises = classicMatchIds.reduce((acc, matchId) => {
            const group = groupedClassicItemsByMatchId[matchId];

            if (group.length > 1) {
              return [
                ...acc,
                new Promise((res) => {
                  dispatch(
                    requestMyMatchMerge(
                      { matchId: parseInt(matchId, 10) },
                      {
                        onFinally: res,
                      },
                    ),
                  );
                }),
              ];
            }

            return acc;
          }, []);

          try {
            await Promise.all(promises);
          } finally {
            dispatch(basketSwitchMode({ mode }));
          }
        }
      }

      if (actionType === BasketActionTypes.ADD_ITEMS) {
        const { bets, isRepeatBet, isFromHistory } = actionPayload ?? {};

        bets.forEach((bet) => {
          if (Array.isArray(bet)) {
            bet.forEach((subOutcomes) => {
              const { matchId, betId, oddId } = subOutcomes;

              dispatch(
                basketOutcomeSelected({
                  oddId,
                  matchId,
                  betId,
                  isRepeatBet,
                  isFromHistory,
                }),
              );
            });
          } else {
            const { matchId, betId, oddId } = bet;

            dispatch(
              basketOutcomeSelected({
                oddId,
                matchId,
                betId,
                isRepeatBet,
                isFromHistory,
              }),
            );
          }
        });
      }

      if (
        actionType === BasketActionTypes.CLEAR ||
        actionType === BasketActionTypes.ADD_ITEM ||
        actionType === BasketActionTypes.DEL_ITEM ||
        actionType === BasketActionTypes.UPDATE_ITEM_IS_FROM_MULTICHOICE ||
        actionType === MyMatchActionTypes.MERGE ||
        actionType === MyMatchActionTypes.SPLIT ||
        actionType === BasketActionTypes.SUBMIT_OK ||
        actionType === BasketActionTypes.GET_SELECTION
      ) {
        const match = history?.location?.pathname.match(
          new RegExp(RoutePatterns.MATCH.replace(':matchId', '(\\d+)(.*)$')),
        );

        if (match) {
          const [, matchIdasStr] = match;
          const matchIdFromRoute = parseInt(matchIdasStr, 10);

          const { matchId: targetedMatchId = matchIdFromRoute } = actionPayload ?? {};

          const nexted = next(action);
          const triggerLoading = actionType !== BasketActionTypes.CLEAR;

          dispatch(getFutureOdds({ matchId: targetedMatchId, triggerLoading, shouldHideOddUpdateIndicators: true }));
          return nexted;
        }
      }
    }

    return next(action);
  };

export default basketMiddleware;
